import {ConfirmationDialog, FormDialog} from '@management-ui/core';
import {Button, styled} from '@mui/material';
import * as React from 'react';
import {useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import Currency from '../../../../components/Currency';
import {ServiceContext} from '../../../../components/Services';
import moment from '../../../../moment';
import ProfitTargetForm from '../../forms/ProfitTargetForm';
import RunningCostForm from '../../forms/RunningCostForm';
import {BlockColumn, BlockContainer, BlockRow, Blocks, Buttons, Container, Header, ReportContent} from './styles';
import Wrapper from './Wrapper';
import YearSelector from './YearSelector';

export const TargetColumn = styled(BlockColumn)(({theme}) => ({
  '& > div': {

    '&:nth-of-type(1) > h6': {
      color: theme.palette.profitReportTier.breakEven,
    },

    '&:nth-of-type(2) > h6': {
      color: theme.palette.profitReportTier.good,
    },

    '&:nth-of-type(3) > h6': {
      color: theme.palette.profitReportTier.overAchieve,
    }
  },
}));

export const CostColumn = styled(BlockColumn)(({theme}) => ({
  '& > div': {
    '& > h6': {
      fontSize: '0.95em',
      margin: 0,
      padding: theme.spacing(0.5, 0),
    },
  },
}));

const Target = ({target, onEdit}) => (
  <BlockContainer>
    <h3>Targets</h3>
    <BlockRow>
      <TargetColumn className="interactive">
        <h4 onClick={() => onEdit()}>Annual</h4>
        <div>
          <h5>Break Even:</h5>
          <h6><Currency amount={target?.break_even}/></h6>
        </div>
        <div>
          <h5>Good:</h5>
          <h6><Currency amount={target?.good}/></h6>
        </div>
        <div>
          <h5>Over Achieve:</h5>
          <h6><Currency amount={target?.over_achieve}/></h6>
        </div>
      </TargetColumn>
    </BlockRow>
    <BlockRow>
      <TargetColumn>
        <h4>Daily</h4>
        <div>
          <h5>Break Even:</h5>
          <h6><Currency amount={target?.daily?.break_even}/></h6>
        </div>
        <div>
          <h5>Good:</h5>
          <h6><Currency amount={target?.daily?.good}/></h6>
        </div>
        <div>
          <h5>Over Achieve:</h5>
          <h6><Currency amount={target?.daily?.over_achieve}/></h6>
        </div>
      </TargetColumn>
    </BlockRow>
  </BlockContainer>
);

const Month = ({year, month, costs, onEdit}) => (
  <BlockContainer>
    <h3>{moment(`${year}-${`${month.month}`.padStart(2, '0')}-01`).format('MMMM')}</h3>
    <BlockRow>
      <TargetColumn>
        <h4>Targets ({month.working_days} Working Days)</h4>
        <div>
          <h5>Break Even:</h5>
          <h6><Currency amount={month.break_even}/></h6>
        </div>
        <div>
          <h5>Good:</h5>
          <h6><Currency amount={month.good}/></h6>
        </div>
        <div>
          <h5>Over Achieve:</h5>
          <h6><Currency amount={month.over_achieve}/></h6>
        </div>
      </TargetColumn>
    </BlockRow>
    <BlockRow>
      <CostColumn className="interactive">
        <h4 onClick={() => onEdit(month)}>Running Costs{costs?.notes ? ' *' : ''}</h4>
        <div>
          <h6><Currency amount={costs?.amount}/></h6>
        </div>
      </CostColumn>
    </BlockRow>
  </BlockContainer>
);

const Profits = () => {
  const services = useContext(ServiceContext);
  const {user} = useSelector(state => state['auth']);
  const [loading, setLoading] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [target, setTarget] = useState(null);
  const [costs, setCosts] = useState([]);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showCostsForm, setShowCostsForm] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);

  const loadCosts = useCallback(() => {
    services.runningCost.getCosts({filter: {year: selectedYear}}, 1, 100).then(response => {
      setCosts(response.data);
      setLoading('');
    }).catch(() => {
      setCosts([]);
      setLoading('');
    })
  }, [services, selectedYear]);

  useEffect(() => {
    if (selectedYear) {
      setLoading(`Loading ${selectedYear}...`);
      services.profitTarget.getTarget(selectedYear).then(target => {
        setTarget(target);
        loadCosts();
      }).catch(() => {
        setTarget(null);
        setLoading('');
      });
    } else {
      setSelectedYear(parseInt(moment().format('YYYY')));
    }
  }, [services, selectedYear, loadCosts]);

  const handleGenerate = useCallback((confirmed) => {
    if (confirmed) {
      setLoading(`Requesting Report for ${selectedYear}...`);
      services.reporting.generateProfitReport(selectedYear)
        .then(() => setLoading(''))
        .catch(() => setLoading(''));
    }
    setShowGenerate(false);
  }, [services, selectedYear]);

  const handleTargetSaved = useCallback((saved) => {
    setTarget(saved);
    setShowTargetForm(false);
  }, []);

  const handleCostSaved = useCallback(() => {
    setShowCostsForm(false);
    loadCosts();
  }, [loadCosts]);

  return (
    <Wrapper title="Profits" loading={loading !== ''} crumbs={[
      ...Wrapper.standardCrumbs,
      {title: 'Profits'},
    ]}>
      <Container className={loading === '' ? '' : 'loading'}>
        <Buttons>
          <Button onClick={() => setShowGenerate(true)} disabled={!!loading || !target} variant="contained">
            Generate Report
          </Button>
        </Buttons>
        <ReportContent>
          <YearSelector year={selectedYear} onChange={setSelectedYear} loading={loading}/>
          <Blocks>
            <Target target={target} onEdit={() => setShowTargetForm(true)}/>
            {target?.months ? (
              <>
                <Header>Months</Header>
                {target.months.map((month, index) => (
                  <Month
                    year={target.year}
                    month={month}
                    costs={costs.find((cost) => month.month === cost.month && `${selectedYear}` === `${cost.year}`)}
                    key={index}
                    onEdit={(selected) => {
                      setSelectedMonth(selected);
                      setShowCostsForm(true);
                    }}
                  />
                ))}
              </>
            ) : null}
          </Blocks>
        </ReportContent>
      </Container>
      <FormDialog
        open={showTargetForm}
        title={`Targets for ${selectedYear}`}
        maxWidth="sm"
        render={useCallback((props) => (
          <ProfitTargetForm target={target ?? {}} year={selectedYear} onSaved={handleTargetSaved} {...props} />
        ), [target, selectedYear, handleTargetSaved])}
        onClose={() => setShowTargetForm(false)}/>
      <FormDialog
        open={showCostsForm}
        title={selectedMonth ? `Running Costs for ${moment(`${selectedYear}-${`${selectedMonth.month}`.padStart(2, '0')}-01`).format('MMMM YYYY')}` : ''}
        maxWidth="sm"
        render={useCallback((props) => (
          <RunningCostForm
            month={selectedMonth ?? {}}
            cost={(selectedMonth ? costs.find((cost) => selectedMonth.month === cost.month && `${selectedYear}` === `${cost.year}`) : undefined) ?? {}}
            year={selectedYear}
            onSaved={handleCostSaved} {...props}
          />
        ), [selectedMonth, selectedYear, costs, handleCostSaved])}
        onClose={() => setShowCostsForm(false)}
      />
      <ConfirmationDialog
        open={showGenerate}
        title={`Generate Profit Report for ${selectedYear}`}
        message={`Do you want to generate a profit report for ${selectedYear}? It may take a while so will be sent to you via email at ${user.email}`}
        onClose={handleGenerate}
      />
    </Wrapper>
  );
}

export default Profits;
